'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import type { SupportedGame } from '@/config/supportedGames';
import { Game } from '@/constants/game.const';
import { useGetGameFromRouteParams } from '@/hooks/useGetGameFromParams.hook';
import { Button } from './button';
import { GameIcon } from './GameIcon';
import { TrustpilotIcon } from './icons/socials/TrustpilotIcon';

export const GAME_SELECTION_NAVIGATION_ID = 'game-selection-nav';

// Regex for removing leading slashes, defined at top level for performance
const LEADING_SLASHES_REGEX = /^\/+/;

export const GameSelectionNavigation = () => {
  return (
    <div
      className="sticky top-26 z-30 flex w-full flex-row bg-medium-gray sm:top-16"
      id={GAME_SELECTION_NAVIGATION_ID}
    >
      <GameSelectionItems />
    </div>
  );
};

export function GameSelectionItems() {
  const { game } = useGetGameFromRouteParams(); // e.g. Game.CS2, or undefined for All
  const pathname = usePathname() || '/';

  const isAllActive = !game;
  const isDotaActive = game === Game.DOTA;
  const isCS2Active = game === Game.CS2;

  const allNavigationLink = useMemo(
    () => buildGamePath(pathname, game, undefined), // => "All"
    [pathname, game]
  );

  const dotaNavigationLink = useMemo(
    () => buildGamePath(pathname, game, Game.DOTA),
    [pathname, game]
  );

  const cs2NavigationLink = useMemo(
    () => buildGamePath(pathname, game, Game.CS2),
    [pathname, game]
  );

  return (
    <div className="container mx-auto flex w-full max-w-(--breakpoint-xl) items-center justify-between px-4 py-2">
      {/* All */}
      <div className="flex gap-4">
        <Button
          aria-current={isAllActive ? 'page' : undefined}
          as="link"
          href={allNavigationLink}
          size="sm"
          variant={isAllActive ? 'default' : 'ghost'}
        >
          All
        </Button>

        {/* Dota */}
        <Button
          aria-current={isDotaActive ? 'page' : undefined}
          as="link"
          href={dotaNavigationLink}
          size="sm"
          variant={isDotaActive ? 'default' : 'ghost'}
        >
          <GameIcon
            aria-hidden="true"
            className="h-4.5 w-4.5 text-white"
            game={Game.DOTA}
          />
          Dota 2
        </Button>

        {/* CS */}
        <Button
          aria-current={isCS2Active ? 'page' : undefined}
          as="link"
          href={cs2NavigationLink}
          size="sm"
          variant={isCS2Active ? 'default' : 'ghost'}
        >
          <GameIcon
            aria-hidden="true"
            className="h-4.5 w-4.5 text-white"
            game={Game.CS2}
          />
          CS 2
        </Button>
      </div>

      <a
        className="hidden items-center gap-1.5 sm:inline-flex"
        href="https://www.trustpilot.com/review/gocore.gg"
        rel="noopener"
        target="_blank"
      >
        <span className="font-normal text-sm/6.5 text-white">
          See our{' '}
          <span className="font-semibold text-light-blue">13 reviews</span> on{' '}
        </span>
        <TrustpilotIcon />
        <span className="font-normal text-sm/6.5 text-white">Trustpilot</span>
      </a>
    </div>
  );
}

function buildGamePath(
  pathname: string,
  currentGame: SupportedGame | undefined,
  targetGame: SupportedGame | undefined
) {
  const segments = pathname.replace(LEADING_SLASHES_REGEX, '').split('/');
  const firstSegment = segments[0] || '';
  const isFirstSegmentGame = currentGame === firstSegment;

  // Special case for pickems when switching to "All"
  if (segments.length >= 3 && pathname.includes('pickems') && !targetGame) {
    return '/pickems';
  }

  if (targetGame) {
    // Switching to a specific game
    if (isFirstSegmentGame) {
      // Replace existing game prefix
      segments[0] = targetGame;
    } else {
      // Add new game prefix
      segments.unshift(targetGame);
    }
  } else if (isFirstSegmentGame) {
    // Switching to "All" - remove game prefix if present
    segments.shift();
  }

  const newPath = `/${segments.join('/')}`;
  return newPath === '/' ? '/' : newPath;
}
